import { useTranslation } from 'react-i18next'
import type { BenchmarkCohort } from './benchmark-utils'

/**
 * `n`, the exclusions and the three quality measures, always visible — never
 * on hover. A benchmark without its `n` is a rumour.
 */
interface Props {
    cohort: BenchmarkCohort
    window: { from: string; to: string }
    locale: string
    testId?: string
}

const EXCLUSION_ORDER = ['missing_denominator', 'no_spend', 'currency_mismatch', 'other_currency']

export function CohortFootnote({ cohort, window, locale, testId = 'benchmark-cohort' }: Props) {
    const { t } = useTranslation('benchmark')
    const pct = (v: number | null | undefined) =>
        v === null || v === undefined ? null : new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(v)
    const date = (iso: string) => new Intl.DateTimeFormat(locale, { month: '2-digit', year: 'numeric' }).format(new Date(iso))

    const parts: { key: string; text: string }[] = [{ key: 'n', text: t('cohort.n', { count: cohort.n }) }]
    for (const reason of EXCLUSION_ORDER) {
        const count = cohort.excluded[reason]
        if (count) parts.push({ key: reason, text: t(`cohort.excluded.${reason}`, { count }) })
    }
    const coverage = pct(cohort.quality.allocation_coverage)
    if (coverage) parts.push({ key: 'allocation', text: t('cohort.allocation_coverage', { value: coverage }) })
    const net = pct(cohort.quality.net_amount_completeness)
    if (net) parts.push({ key: 'net', text: t('cohort.net_amount_completeness', { value: net }) })
    const denominator = pct(cohort.quality.denominator_completeness)
    if (denominator) parts.push({ key: 'denominator', text: t('cohort.denominator_completeness', { value: denominator }) })
    if (cohort.declared_only.count) parts.push({ key: 'declared', text: t('cohort.declared_only', { count: cohort.declared_only.count }) })
    if (cohort.undated_lines) parts.push({ key: 'undated', text: t('cohort.undated_lines', { count: cohort.undated_lines }) })
    parts.push({ key: 'window', text: t('cohort.window', { from: date(window.from), to: date(window.to) }) })

    return (
        <div className="space-y-1" data-testid={testId}>
            <p className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                {parts.map((p, i) => (
                    <span key={p.key} data-testid={`${testId}-${p.key}`}>
                        {i > 0 && <span aria-hidden className="mr-2">·</span>}
                        {p.text}
                    </span>
                ))}
            </p>
            {cohort.suppressed && (
                <p className="text-xs text-foreground" data-testid={`${testId}-suppressed`}>
                    {t('cohort.suppressed', { count: cohort.n })}
                </p>
            )}
            {cohort.stats_withheld && (
                <p className="text-xs text-foreground" data-testid={`${testId}-withheld`}>
                    {t('cohort.withheld')}
                </p>
            )}
            {cohort.rounded && !cohort.stats_withheld && (
                <p className="text-xs text-muted-foreground">{t('cohort.rounded')}</p>
            )}
        </div>
    )
}
